import { Checkbox, CheckboxGroup, Grid, Select, VStack } from "@chakra-ui/react";
import PatientProfileInlineEditableRow from "../../../../../entityProfileModal/components/EntityProfileInlineEditableRow";
import type { PatientProfilePatient } from "../../../../patientProfileModal.types";
import type { PreparednessDraft, PreparednessField } from "../emergencyPreparedness.types";
import {
  evacuationLocationOptions,
  evacuationZoneOptions,
  getElectricEquipmentDependencyOptions,
  getPreparednessDisplayValue,
  mobilityStatusOptions,
  preparednessFields,
  priorityCodeOptions,
  splitElectricEquipmentDependency,
} from "../emergencyPreparedness.utils";

export function EmergencyPreparednessFields(props: {
  activeField: PreparednessField | null;
  draft: PreparednessDraft;
  isEditing: boolean;
  patient: PatientProfilePatient;
  onActivate: (field: PreparednessField) => void;
  onCancel: () => void;
  onChangeDraft: (draft: PreparednessDraft) => void;
  onCommit: (field: PreparednessField) => void;
}) {
  return (
    <Grid
      gap={8}
      gridColumn="1 / -1"
      templateColumns={{ base: "1fr", lg: "repeat(2, minmax(0, 1fr))" }}
    >
      <VStack alignItems="stretch" spacing={0}>
        {preparednessFields.slice(0, 3).map((field) => (
          <PreparednessRow
            key={field.field}
            activeField={props.activeField}
            draft={props.draft}
            field={field.field}
            isEditing={props.isEditing}
            label={field.label}
            patient={props.patient}
            onActivate={props.onActivate}
            onCancel={props.onCancel}
            onChangeDraft={props.onChangeDraft}
            onCommit={props.onCommit}
          />
        ))}
      </VStack>
      <VStack alignItems="stretch" spacing={0}>
        {preparednessFields.slice(3).map((field) => (
          <PreparednessRow
            key={field.field}
            activeField={props.activeField}
            draft={props.draft}
            field={field.field}
            isEditing={props.isEditing}
            label={field.label}
            patient={props.patient}
            onActivate={props.onActivate}
            onCancel={props.onCancel}
            onChangeDraft={props.onChangeDraft}
            onCommit={props.onCommit}
          />
        ))}
      </VStack>
    </Grid>
  );
}

function PreparednessRow(props: {
  activeField: PreparednessField | null;
  draft: PreparednessDraft;
  field: PreparednessField;
  isEditing: boolean;
  label: string;
  patient: PatientProfilePatient;
  onActivate: (field: PreparednessField) => void;
  onCancel: () => void;
  onChangeDraft: (draft: PreparednessDraft) => void;
  onCommit: (field: PreparednessField) => void;
}) {
  const value = getPreparednessDisplayValue(props.field, props.draft, props.patient);

  return (
    <PatientProfileInlineEditableRow
      activeField={props.activeField}
      emptyPlaceholder={props.isEditing}
      field={props.field}
      isEditable={props.isEditing}
      label={props.label}
      value={value}
      onActivate={props.onActivate}
      onCancel={props.onCancel}
      onCommit={() => props.onCommit(props.field)}
    >
      <PreparednessInlineEditor
        draft={props.draft}
        field={props.field}
        onChange={props.onChangeDraft}
      />
    </PatientProfileInlineEditableRow>
  );
}

function PreparednessInlineEditor(props: {
  draft: PreparednessDraft;
  field: PreparednessField;
  onChange: (draft: PreparednessDraft) => void;
}) {
  const updateField = <TKey extends PreparednessField>(key: TKey, value: PreparednessDraft[TKey]) =>
    props.onChange({ ...props.draft, [key]: value });

  if (props.field === "priorityCode") {
    return (
      <Select
        autoFocus
        size="sm"
        value={props.draft.priorityCode == null ? "" : String(props.draft.priorityCode)}
        onChange={(event) =>
          updateField("priorityCode", event.target.value === "" ? null : Number(event.target.value))
        }
      >
        <option value="">Select priority</option>
        {priorityCodeOptions.map((priorityCode) => (
          <option key={priorityCode} value={priorityCode}>
            {priorityCode}
          </option>
        ))}
      </Select>
    );
  }

  if (props.field === "evacuationZone") {
    return (
      <Select
        autoFocus
        size="sm"
        value={props.draft.evacuationZone ?? ""}
        onChange={(event) => updateField("evacuationZone", event.target.value || null)}
      >
        <option value="">Select zone</option>
        {evacuationZoneOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
    );
  }

  if (props.field === "evacuationLocation") {
    return (
      <Select
        autoFocus
        size="sm"
        value={props.draft.evacuationLocation ?? ""}
        onChange={(event) => updateField("evacuationLocation", event.target.value || null)}
      >
        <option value="">Select location</option>
        {evacuationLocationOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
    );
  }

  if (props.field === "mobilityStatus") {
    return (
      <Select
        autoFocus
        size="sm"
        value={props.draft.mobilityStatus ?? ""}
        onChange={(event) => updateField("mobilityStatus", event.target.value || null)}
      >
        <option value="">Select mobility status</option>
        {mobilityStatusOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
    );
  }

  const selectedEquipment = splitElectricEquipmentDependency(
    props.draft.electricEquipmentDependency
  );

  return (
    <CheckboxGroup
      value={selectedEquipment}
      onChange={(values) =>
        updateField(
          "electricEquipmentDependency",
          values.length === 0 ? null : values.map(String).join(",")
        )
      }
    >
      <VStack alignItems="stretch" maxH="220px" overflowY="auto" spacing={2}>
        {getElectricEquipmentDependencyOptions(props.draft.electricEquipmentDependency).map(
          (option) => (
            <Checkbox key={option} value={option}>
              {option}
            </Checkbox>
          )
        )}
      </VStack>
    </CheckboxGroup>
  );
}
